import React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";

type WarningModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description: string;
  continueLabel?: string;
  cancelLabel?: string;
  onContinue: () => void | Promise<void>;
  isLoading?: boolean;
  colorScheme?: string;
};

const WarningModal: React.FC<WarningModalProps> = ({
  isOpen,
  onClose,
  title,
  description,
  onContinue,
  continueLabel = "Continue",
  cancelLabel = "Cancel",
  isLoading = false,
  colorScheme = "red",
}) => {
  const [isPending, setIsPending] = React.useState(false);

  const handleContinue = async () => {
    setIsPending(true);
    try {
      await Promise.resolve(onContinue());
      onClose();
    } catch {
      // Keep the modal open when the action fails so callers can retry.
    } finally {
      setIsPending(false);
    }
  };

  const grayColor = useColorModeValue("gray.600", "gray.400");

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered motionPreset="scale">
      <ModalOverlay />
      <ModalContent
        w={{ base: "80vw", md: "full" }}
        maxW={{ base: "80vw", md: "500px" }}
      >
        <ModalHeader>{title}</ModalHeader>

        <ModalBody>
          <Text fontSize="sm" color={grayColor}>
            {description}
          </Text>
        </ModalBody>

        <ModalFooter>
          <Button
            variant="ghost"
            mr={3}
            onClick={onClose}
            isDisabled={isPending || isLoading}
          >
            {cancelLabel}
          </Button>

          <Button
            colorScheme={colorScheme}
            onClick={handleContinue}
            isLoading={isPending || isLoading}
          >
            {continueLabel}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default WarningModal;
